import { io, Socket } from 'socket.io-client';
import { BASE_URL } from '../config';

// Socket.IO se connecte à la racine du serveur (sans /api)
const SOCKET_URL = BASE_URL;

console.log('🔧 socketService - SOCKET_URL:', SOCKET_URL);

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: any;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl?: string;
  createdAt: string;
  isRead: boolean;
}

class SocketService {
  private socket: Socket | null = null;
  private userId: number | null = null;
  private listeners: Map<string, Function[]> = new Map();

  /**
   * Se connecter au serveur Socket.IO
   */
  connect(userId: number | string) {
    console.log('🔌 socketService.connect() appelé avec userId:', userId, 'type:', typeof userId);
    
    // Si déjà connecté ET authentifié avec le même userId, ne rien faire
    if (this.socket?.connected && this.userId === (typeof userId === 'string' ? parseInt(userId) : userId)) {
      console.log('✅ Socket déjà connecté et authentifié pour user:', this.userId);
      return;
    }

    // Déconnecter l'ancien socket s'il existe
    if (this.socket) {
      console.log('🔌 Déconnexion de l\'ancien socket...');
      this.socket.disconnect();
      this.socket = null;
    }

    this.userId = typeof userId === 'string' ? parseInt(userId) : userId;
    console.log('🔢 userId converti en number:', this.userId);

    console.log('🔌 Connexion Socket.IO à', SOCKET_URL);
    
    this.socket = io(SOCKET_URL, {
      path: '/socket.io/',
      auth: {
        token: localStorage.getItem('token') || sessionStorage.getItem('token')
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      withCredentials: true
    });

    // Événements de connexion
    this.socket.on('connect', () => {
      console.log('✅ Socket.IO connecté:', this.socket?.id);
      
      console.log('🔐 Handshake JWT accepté pour user:', this.userId);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket.IO déconnecté:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Erreur connexion Socket.IO:', error);
    });

    // Écouter les nouvelles notifications
    this.socket.on('new_notification', (notification: Notification) => {
      console.log('🔔 Nouvelle notification reçue:', notification);
      this.emit('new_notification', notification);
    });

    // Écouter les notifications marquées comme lues
    this.socket.on('notification_read', (data: { notificationId: number }) => {
      console.log('✅ Notification marquée comme lue:', data.notificationId);
      this.emit('notification_read', data);
    });

    // Écouter toutes les notifications marquées comme lues
    this.socket.on('all_notifications_read', () => {
      console.log('✅ Toutes les notifications marquées comme lues');
      this.emit('all_notifications_read');
    });

    // Écouter les nouvelles demandes de rappel / contact
    this.socket.on('contact_request_created', (data: any) => {
      console.log('📞 Nouvelle demande de rappel reçue via Socket:', data);
      this.emit('contact_request_created', data);
      this.emit('new_contact_request', data);
    });

    this.socket.on('new_contact_request', (data: any) => {
      console.log('📞 Nouvelle demande de rappel reçue via Socket (alias):', data);
      this.emit('new_contact_request', data);
    });

    // Écouter les changements de statut de disponibilité des techniciens
    this.socket.on('technician_status_changed', (data: { user_id: number; availability_status: string }) => {
      console.log('🔄 Statut technicien modifié via Socket:', data);
      this.emit('technician_status_changed', data);
    });
  }

  /**
   * Se déconnecter du serveur Socket.IO
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Déconnexion Socket.IO');
      this.socket.disconnect();
      this.socket = null;
      this.userId = null;
      this.listeners.clear();
    }
  }

  /**
   * Marquer une notification comme lue
   */
  markAsRead(notificationId: number) {
    if (this.socket?.connected) {
      this.socket.emit('mark_read', notificationId);
    }
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  markAllAsRead(_userId: number | string) {
    if (this.socket?.connected) {
      // Le serveur dérive toujours l'utilisateur du JWT du handshake.
      this.socket.emit('mark_all_read');
    }
  }

  /**
   * Écouter un événement
   */
  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);
  }

  /**
   * Arrêter d'écouter un événement
   */
  off(event: string, callback: Function) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Émettre un événement aux listeners locaux
   */
  private emit(event: string, ...args: any[]) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(...args));
    }
  }

  /**
   * Vérifier si le socket est connecté
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// Export singleton
const socketService = new SocketService();
export default socketService;
